/**
 * HeroBanner Component - Manages the hero section with search form
 * Integrates the search form and handles navigation to search results
 */

import { HeroBanner as IHeroBanner, SearchData } from '../types/components';
import { SearchFormComponent } from './SearchFormComponent';

export class HeroBanner implements IHeroBanner {
  title: string = '';
  subtitle: string = '';
  backgroundImage: string = '';
  searchForm!: SearchFormComponent;
  
  private heroElement: HTMLElement;

  constructor(heroElement: HTMLElement) {
    this.heroElement = heroElement;
    
    // Initialize properties from DOM
    this.initializeFromDOM();
    
    // Initialize search form
    this.initializeSearchForm();
  }

  private initializeFromDOM(): void {
    const titleElement = this.heroElement.querySelector('.hero__title') as HTMLElement;
    const subtitleElement = this.heroElement.querySelector('.hero__subtitle') as HTMLElement;
    
    this.title = titleElement?.textContent || 'Découvrez le monde avec Kiwi Voyages';
    this.subtitle = subtitleElement?.textContent || 'Comparez et réservez vos voyages au meilleur prix';
    this.backgroundImage = 'placeholder'; // Will be replaced with actual image
  }

  private initializeSearchForm(): void {
    const formElement = this.heroElement.querySelector('#hero-search-form') as HTMLFormElement;
    
    if (!formElement) {
      throw new Error('Hero search form not found');
    }

    // Create search form component with submit handler
    this.searchForm = new SearchFormComponent(formElement, this.handleSearchSubmit.bind(this));
  }

  private async handleSearchSubmit(searchData: SearchData): Promise<void> {
    try {
      // Log search data for debugging
      console.log('Search submitted:', searchData);
      
      // Build search URL based on search type
      const searchUrl = this.buildSearchUrl(searchData);
      
      // Navigate to search results page
      await this.navigateToSearch(searchUrl, searchData);
      
    } catch (error) {
      console.error('Error handling search submission:', error);
      throw error; // Re-throw to be handled by SearchFormComponent
    }
  }

  private buildSearchUrl(searchData: SearchData): string {
    const basePath = (window as any).__BASE_PATH__ || '';
    const baseUrls: Record<string, string> = {
      flights: `${basePath}/flights.html`,
      hotels: `${basePath}/hotels.html`,
      trains: `${basePath}/trains.html`
    };

    const baseUrl = baseUrls[searchData.searchType];
    const params = new URLSearchParams();

    // Add search parameters
    params.set('destination', searchData.destination);
    params.set('departure', searchData.departureDate.toISOString().split('T')[0]!);
    
    if (searchData.returnDate) {
      params.set('return', searchData.returnDate.toISOString().split('T')[0]!);
    }
    
    params.set('passengers', searchData.passengers.toString());

    return `${baseUrl}?${params.toString()}`;
  }

  private async navigateToSearch(url: string, searchData: SearchData): Promise<void> {
    // Store search data in sessionStorage for the results page
    sessionStorage.setItem('searchData', JSON.stringify(searchData));
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Navigate to search results
    window.location.href = url;
  }

  // Public methods
  public updateTitle(title: string): void {
    this.title = title;
    const titleElement = this.heroElement.querySelector('.hero__title') as HTMLElement;
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  public updateSubtitle(subtitle: string): void {
    this.subtitle = subtitle;
    const subtitleElement = this.heroElement.querySelector('.hero__subtitle') as HTMLElement;
    if (subtitleElement) {
      subtitleElement.textContent = subtitle;
    }
  }

  public setDestination(destination: string): void {
    if (this.searchForm) {
      this.searchForm.setDestination(destination);
    }
  }

  public resetSearchForm(): void {
    if (this.searchForm) {
      this.searchForm.reset();
    }
  }

  public getSearchType(): 'flights' | 'hotels' | 'trains' {
    return this.searchForm ? this.searchForm.getSearchType() : 'flights';
  }

  // Static factory method
  public static initialize(): HeroBanner | null {
    const heroElement = document.querySelector('.hero') as HTMLElement;
    
    if (!heroElement) {
      console.warn('Hero element not found');
      return null;
    }

    try {
      return new HeroBanner(heroElement);
    } catch (error) {
      console.error('Failed to initialize HeroBanner:', error);
      return null;
    }
  }
}